import { format } from "date-fns-jalali";
import {
  calculateStayNightDuration,
  jalaliToGregorian,
} from "@/shared/libs/date";
import { createDetailRoomList, createRoomsPattern } from "@/shared/libs/utils";
import type { HotelDetailBySlugResponse } from "@/types/services/hotel/apis/Detail";
import type { HotelDetailV2BookingModel } from "../model/hotelDetailV2.model";

type BookingSearch = Pick<SearchParams, "checkIn" | "checkOut" | "rooms">;

export function mapHotelBookingToV2(
  response: HotelDetailBySlugResponse | undefined,
  search: BookingSearch,
): HotelDetailV2BookingModel {
  const nights = Math.max(
    1,
    calculateStayNightDuration(search.checkIn, search.checkOut) || 1,
  );
  const guests = parseGuests(search.rooms);
  const existedRooms = response?.results?.existed_rooms ?? [];
  const ratePlans = createDetailRoomList(existedRooms).filter(
    (room) =>
      room.rate_plans.inventory > 0 &&
      room.rate_plans.price > 0,
  );
  const cheapestRate = ratePlans[0]?.rate_plans;
  const originalTotalPrice = getOriginalTotalPrice(
    cheapestRate?.price,
    cheapestRate?.total_price,
  );
  const discountPercent = getDiscountPercent(
    cheapestRate?.price,
    originalTotalPrice,
  );

  return {
    checkIn: search.checkIn,
    checkOut: search.checkOut,
    checkInLabel: format(jalaliToGregorian(search.checkIn), "d MMMM yyyy"),
    checkOutLabel: format(jalaliToGregorian(search.checkOut), "d MMMM yyyy"),
    nights,
    roomsParam: search.rooms,
    roomCount: guests.roomCount,
    adults: guests.adults,
    children: guests.children,
    available: Boolean(response?.succeed && cheapestRate),
    availabilityMessage: response?.message,
    availableRoomTypes: existedRooms.length,
    remainingInventory: existedRooms.reduce(
      (total, room) => total + Math.max(0, room.remain || 0),
      0,
    ),
    nightlyPrice: cheapestRate
      ? Math.round(cheapestRate.price / nights)
      : undefined,
    originalNightlyPrice: originalTotalPrice
      ? Math.round(originalTotalPrice / nights)
      : undefined,
    discountPercent,
    totalPrice: cheapestRate?.price,
    originalTotalPrice,
    cancelable: cheapestRate?.cancelable,
    mealLabel: normalizeMealLabel(cheapestRate?.meal_type_included),
    accommodationKey: response?.accommodation_key,
    rooms: ratePlans.map((room) => {
      const roomOriginalTotalPrice = getOriginalTotalPrice(
        room.rate_plans.price,
        room.rate_plans.total_price,
      );

      return {
        key: String(room.keyBy),
        name: room.name,
        imageUrl: getRoomImageUrl(room),
        ratePlanName: room.rate_plans.name,
        capacity: room.capacity,
        extraCapacity: room.extra_capacity,
        inventory: room.rate_plans.inventory,
        remaining:
          existedRooms.find((sourceRoom) => sourceRoom.id === room.id)?.remain ??
          room.rate_plans.inventory,
        breakfastIncluded: isMealIncluded(room.rate_plans.meal_type_included),
        mealLabel: normalizeMealLabel(room.rate_plans.meal_type_included),
        cancelable: room.rate_plans.cancelable,
        totalPrice: room.rate_plans.price,
        originalTotalPrice: roomOriginalTotalPrice,
        nightlyPrice: Math.round(room.rate_plans.price / nights),
        originalNightlyPrice: roomOriginalTotalPrice
          ? Math.round(roomOriginalTotalPrice / nights)
          : undefined,
        discountPercent: getDiscountPercent(
          room.rate_plans.price,
          roomOriginalTotalPrice,
        ),
        source: room,
      };
    }),
  };
}

type RoomImageCandidate = {
  path?: unknown;
  url?: unknown;
  image?: unknown;
  src?: unknown;
  type?: unknown;
};

function getRoomImageUrl(room: unknown) {
  if (!isRecord(room)) return undefined;

  return firstImageUrl([
    room.images,
    room.image,
    room.picture,
    room.thumbnail,
    room.photo,
    room.photos,
  ]);
}

function firstImageUrl(candidates: unknown[]): string | undefined {
  for (const candidate of candidates) {
    const imageUrl = normalizeImageCandidate(candidate);
    if (imageUrl) return imageUrl;
  }

  return undefined;
}

function normalizeImageCandidate(candidate: unknown): string | undefined {
  if (typeof candidate === "string") return optionalText(candidate);
  if (Array.isArray(candidate)) {
    const normalizedImages = candidate
      .map((item) => normalizeImageObject(item))
      .filter((image): image is RoomImageObject => Boolean(image));
    const preferredImage =
      normalizedImages.find((item) => isCompactImageType(item.type)) ??
      normalizedImages.find((item) => item.url);

    return preferredImage?.url;
  }

  return normalizeImageObject(candidate)?.url;
}

function normalizeImageObject(candidate: unknown) {
  if (!isRecord(candidate)) return undefined;
  const image = candidate as RoomImageCandidate;
  const url =
    optionalUnknownText(image.path) ??
    optionalUnknownText(image.url) ??
    optionalUnknownText(image.image) ??
    optionalUnknownText(image.src);

  if (!url) return undefined;

  return {
    url,
    type: optionalUnknownText(image.type),
  };
}

type RoomImageObject = NonNullable<ReturnType<typeof normalizeImageObject>>;

function isCompactImageType(type: string | undefined) {
  return Boolean(
    type && /^(thumbnail|thumb|th|small|sm|medium|md)$/i.test(type),
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object");
}

function optionalUnknownText(value: unknown) {
  return typeof value === "string" ? optionalText(value) : undefined;
}

function optionalText(value: string | null | undefined) {
  const normalized = value?.trim();
  return normalized || undefined;
}

function parseGuests(roomsParam: string) {
  const rooms = createRoomsPattern("decode", roomsParam || "2");

  return {
    roomCount: rooms.length,
    adults: rooms.reduce((total, room) => total + room.adult, 0),
    children: rooms.reduce((total, room) => total + room.child.length, 0),
  };
}

function normalizeMealLabel(meal?: string | null) {
  if (!meal) return undefined;

  const labels: Record<string, string> = {
    RO: "بدون وعده غذایی",
    BB: "همراه صبحانه",
    HB: "صبحانه و یک وعده غذایی",
    FB: "سه وعده غذایی",
    room_only: "فقط اتاق",
    breakfast: "همراه صبحانه",
    half_board: "صبحانه و یک وعده غذایی",
    full_board: "سه وعده غذایی",
  };

  return labels[meal] || meal;
}

function isMealIncluded(meal?: string | null) {
  return Boolean(meal && !["RO", "room_only"].includes(meal));
}

function getOriginalTotalPrice(
  price: number | undefined,
  totalPrice: number | undefined,
) {
  if (!price || !totalPrice || totalPrice <= price) return undefined;
  return totalPrice;
}

function getDiscountPercent(
  price: number | undefined,
  originalPrice: number | undefined,
) {
  if (!price || !originalPrice || originalPrice <= price) return undefined;
  return Math.trunc(((originalPrice - price) / originalPrice) * 100);
}
